import type { IapPurchaseParams, IapPurchaseResult } from "../types";
import type { Transport } from "../transport";

/**
 * Внутриигровые покупки (IAP). Разраб создаёт товары в
 * `/dev/games/[id]/iap` (id, название, цена в ₽, тип: non-consumable / consumable).
 *
 * SDK.payments.purchase() → открывает в родительском окне форму ЮKassa,
 * после оплаты webhook обновляет `purchases`, SDK получает промисом результат.
 *
 * Расщепление дохода (revenue share) — 70/30 (разраб / платформа) — реализуется
 * в v2 после перехода на ИП/ООО. На MVP все IAP идут оператору (422-ФЗ).
 */
export function createPayments(t: Transport) {
  return {
    /**
     * Инициировать покупку товара. Блокирующая — игра должна pause.
     * Возвращает статус после того как юзер закончил (оплатил или отменил).
     */
    purchase(params: IapPurchaseParams): Promise<IapPurchaseResult> {
      return t.request<IapPurchaseResult>("payments.purchase", params);
    },

    /**
     * Список товаров этой игры с ценами.
     * Полезно для отображения shop-экрана в игре — не надо хардкодить цены.
     */
    getItems(): Promise<
      Array<{
        itemId: string;
        title: string;
        description: string;
        priceRub: number;
        iconUrl: string | null;
        kind: "consumable" | "non_consumable";
      }>
    > {
      return t.request("payments.getItems");
    },

    /**
     * Проверить — купил ли юзер non-consumable товар.
     * Для consumable (расходных) этот метод не подходит — используй `getOwned`.
     */
    has(itemId: string): Promise<boolean> {
      return t.request<boolean>("payments.has", { itemId });
    },

    /**
     * Получить список всех купленных non-consumable товаров текущего юзера.
     */
    getOwned(): Promise<string[]> {
      return t.request<string[]>("payments.getOwned");
    },
  };
}

export type PaymentsApi = ReturnType<typeof createPayments>;
